import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, Check, Hand } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt-BR', name: 'Português (Brasil)', flag: '🇧🇷' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'ht', name: 'Kreyòl Ayisyen', flag: '🇭🇹' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
];

export default function LanguageSwitcher({ className = '' }) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const currentLangCode = i18n.language?.split('-')[0] || 'en';
  const currentLanguage = LANGUAGES.find(l => l.code === i18n.language || l.code === currentLangCode) || LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 hover:bg-gold/20 ${className}`}
        data-testid="language-switcher"
        aria-label="Change language"
        aria-expanded={isOpen}
      >
        <Globe size={14} />
        <span>{currentLanguage.flag}</span>
        <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div 
          className="absolute top-full left-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gold/20 py-2 z-[100] max-h-80 overflow-y-auto"
          data-testid="language-dropdown"
        >
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={(e) => {
                e.stopPropagation();
                changeLanguage(lang.code);
              }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-gold/10 ${
                (currentLanguage.code === lang.code) ? 'bg-gold/10 text-burgundy font-medium' : 'text-charcoal'
              }`}
              data-testid={`lang-${lang.code}`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="flex-1 text-left">{lang.name}</span>
              {currentLanguage.code === lang.code && (
                <Check size={16} className="text-burgundy" />
              )}
            </button>
          ))}
          
          {/* ASL Sign Language Toggle */}
          <div className="border-t border-gold/20 mt-2 pt-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const isEnabled = localStorage.getItem('signLanguageEnabled') === 'true';
                localStorage.setItem('signLanguageEnabled', (!isEnabled).toString());
                window.dispatchEvent(new Event('storage'));
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-purple/10 text-charcoal"
              data-testid="asl-toggle"
            >
              <Hand size={18} className="text-purple" />
              <span className="flex-1 text-left">ASL Sign Language</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                localStorage.getItem('signLanguageEnabled') === 'true' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-500'
              }`}>
                {localStorage.getItem('signLanguageEnabled') === 'true' ? 'ON' : 'OFF'}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
